<template>
  <div class="rh-root">
    <appHeader></appHeader>

    <transition appear name="fade">
      <div class="rh-layout container">

        <RecognitionSidebar />

        <main class="rh-main">

          <div class="rh-page-header">
            <div>
              <h1 class="rh-title">My Recognition History</h1>
              <p class="rh-sub">{{ activeTab === 'received' ? 'Recognitions you have received.' : 'Recognitions you have given.' }}</p>
            </div>
            <select class="rh-select" v-model="selectedPeriod">
              <option value="all">All Time</option>
              <option value="thisMonth">This Month</option>
              <option value="lastMonth">Last Month</option>
              <option value="last3">Last 3 Months</option>
            </select>
          </div>

          <div class="rh-tabs">
            <button class="rh-tab" :class="{ 'rh-tab--active': activeTab === 'received' }" @click="activeTab = 'received'">
              Received
              <span class="rh-tab-count">{{ tabCounts.received }}</span>
            </button>
            <button class="rh-tab" :class="{ 'rh-tab--active': activeTab === 'given' }" @click="activeTab = 'given'">
              Given
              <span class="rh-tab-count">{{ tabCounts.given }}</span>
            </button>
          </div>

          <div class="rh-list">

            <!-- Loading skeleton -->
            <div v-if="isLoading" class="rh-skeleton-wrap">
              <div v-for="n in 4" :key="n" class="rh-skeleton-item">
                <div class="rh-skeleton-avatar"></div>
                <div class="rh-skeleton-body">
                  <div class="rh-skeleton-line rh-skeleton-line--short"></div>
                  <div class="rh-skeleton-line rh-skeleton-line--long"></div>
                </div>
              </div>
            </div>

            <!-- Error state -->
            <div v-else-if="activeError" class="rh-error">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" width="36" height="36">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <p>{{ activeError }}</p>
              <button class="rh-retry-btn" @click="loadHistory">Try again</button>
            </div>

            <!-- Items -->
            <template v-else>
              <div v-for="item in activeList" :key="item.id" class="rh-item">
                <img :src="item.avatar" :alt="item.name" class="rh-avatar" />
                <div class="rh-body">
                  <div class="rh-item-header">
                    <div class="rh-name-wrap">
                      <span class="rh-name">{{ item.name }}</span>
                      <span class="rh-event-tag" :class="`rh-event-tag--${mapEventType(item.event_type)}`">
                        {{ mapEventLabel(item.event_type) }}
                      </span>
                    </div>
                    <div class="rh-meta-right">
                      <div class="rh-date-wrap">
                        <span class="rh-date">{{ item.date }}</span>
                        <span class="rh-time">{{ item.time }}</span>
                      </div>
                      <span class="rh-pts" :class="activeTab === 'received' ? 'rh-pts--positive' : 'rh-pts--negative'">
                        {{ activeTab === 'received' ? '+' : '-' }}{{ item.points }} pts
                      </span>
                      <span v-if="item.is_kudos" class="rh-kudos-badge">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="11" height="11">
                          <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/>
                          <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
                        </svg>
                        Kudos
                      </span>
                    </div>
                  </div>
                  <p class="rh-message">{{ item.message }}</p>
                </div>
              </div>

              <!-- Empty state -->
              <div v-if="!isLoading && activeList.length === 0" class="rh-empty">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" width="40" height="40">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M8 15s1.5-2 4-2 4 2 4 2"/>
                  <line x1="9" y1="9" x2="9.01" y2="9"/>
                  <line x1="15" y1="9" x2="15.01" y2="9"/>
                </svg>
                <p>No recognitions found for this period.</p>
              </div>
            </template>

          </div>

        </main>

        <aside class="rh-panel">
          <div class="rh-panel-sticky">

            <div class="rh-widget">
              <p class="rh-widget-label">My Recognition Balance</p>
              <div class="rh-balance-row">
                <span class="rh-balance-num">{{ balance.toLocaleString() }}</span>
                <span class="rh-balance-unit">pts</span>
              </div>
              <p class="rh-balance-sub">Recognition Points</p>
              <button class="rh-store-btn" @click="$router.push({ path: '/shop/category/1'})">Start Shopping</button>
            </div>

            <div class="rh-widget">
              <p class="rh-widget-title">Summary</p>
              <div class="rh-summary-row">
                <span class="rh-summary-label">Total Received</span>
                <span class="rh-summary-val rh-summary-val--green">+{{ totalReceived }} pts</span>
              </div>
              <div class="rh-summary-row">
                <span class="rh-summary-label">Total Given</span>
                <span class="rh-summary-val rh-summary-val--red">-{{ totalGiven }} pts</span>
              </div>
              <div class="rh-summary-row">
                <span class="rh-summary-label">Times Received</span>
                <span class="rh-summary-val">{{ receivedItems.length }}</span>
              </div>
              <div class="rh-summary-row">
                <span class="rh-summary-label">Times Given</span>
                <span class="rh-summary-val">{{ givenItems.length }}</span>
              </div>
            </div>

          </div>
        </aside>

      </div>
    </transition>

    <appFooter class="rh-footer-desktop"></appFooter>
    <navigationFooter class="rh-footer-mobile"></navigationFooter>
  </div>
</template>

<script>
// ── Replace entire <script> block in RecognitionHistory.vue ──

import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import navigationFooter from "@/components/navigationFooter.vue";
import RecognitionSidebar from "./RecognitionSidebar.vue";
import RecognitionService from "@/services/RecognitionService";

export default {
  name: "RecognitionHistory",

  components: {
    appHeader: Header,
    appFooter: Footer,
    navigationFooter,
    RecognitionSidebar,
  },

  data() {
    return {
      activeTab:      "received",
      selectedPeriod: "all",

      // ── Balance ───────────────────────────────────────
      balance: 0,

      // ── Loading states ────────────────────────────────
      isLoadingReceived: false,
      isLoadingGiven:    false,
      isLoadingBalance:  false,

      // ── Error states ──────────────────────────────────
      errorReceived: null,
      errorGiven:    null,

      // ── Data ─────────────────────────────────────────
      receivedItems: [],
      givenItems:    [],

      // ── Pagination ────────────────────────────────────
      receivedMeta: { total: 0, page: 1, total_pages: 1 },
      givenMeta:    { total: 0, page: 1, total_pages: 1 },
      tabCounts: { received: 0, given: 0 },
    };
  },

  computed: {
    activeList() {
      return this.activeTab === "received" ? this.receivedItems : this.givenItems;
    },

    isLoading() {
      return this.activeTab === "received"
        ? this.isLoadingReceived
        : this.isLoadingGiven;
    },

    activeError() {
      return this.activeTab === "received"
        ? this.errorReceived
        : this.errorGiven;
    },

    totalReceived() {
      return this.receivedMeta.total_points ?? this.receivedItems.reduce((sum, i) => sum + i.points, 0);
    },
    totalGiven() {
      return this.givenMeta.total_points ?? this.givenItems.reduce((sum, i) => sum + i.points, 0);
    },
  },

  watch: {
    // Reload list when tab changes
    activeTab() {
      this.loadHistory();
    },

    // Reload list when period filter changes
    selectedPeriod() {
      this.loadHistory();
    },
  },

  methods: {
    mapEventType(eventType) {
      const map = {
        PeerRecognition: 'peer',
        Birthday:        'birthday',
        WorkAnniversary: 'anniversary',
      };
      return map[eventType] ?? 'peer';
    },

    mapEventLabel(eventType) {
      const map = {
        PeerRecognition: 'Peer Recognition',
        Birthday:        'Birthday',
        WorkAnniversary: 'Work Anniversary',
      };
      return map[eventType] ?? eventType;
    },
    // ── Load history for active tab ───────────────────────
    async loadHistory(type = null) {
      const tab = type ?? this.activeTab;
      const isReceived = tab === "received";

      // Set loading state
      if (isReceived) {
        this.isLoadingReceived = true;
        this.errorReceived     = null;
      } else {
        this.isLoadingGiven = true;
        this.errorGiven     = null;
      }

      try {
        const resp = await RecognitionService.getHistory({
          type:   tab,
          period: this.selectedPeriod,
          page:   1,
          limit:  20,
        });

        const { data, meta } = resp.data;   // ← defined first, then used below

        if (resp.data.counts) {
          this.tabCounts = resp.data.counts;
        }

        if (isReceived) {
          this.receivedItems = data ?? [];
          this.receivedMeta  = meta ?? {};
        } else {
          this.givenItems = data ?? [];
          this.givenMeta  = meta ?? {};
        }

      } catch (err) {
        const message = err.response?.data?.message
          || "Failed to load recognition history.";

        if (isReceived) {
          this.errorReceived = message;
        } else {
          this.errorGiven = message;
        }

      } finally {
        if (isReceived) {
          this.isLoadingReceived = false;
        } else {
          this.isLoadingGiven = false;
        }
      }
    },

    // ── Load balance from quota API ───────────────────────
    async loadBalance() {
      this.isLoadingBalance = true;
      try {
        const resp   = await RecognitionService.getMyQuota();
        this.balance = resp.data.data?.available_points ?? 0;
      } catch (err) {
        console.error("Failed to load balance:", err);
        this.balance = 0;
      } finally {
        this.isLoadingBalance = false;
      }
    },
  },
  // Remove the first watch block entirely, keep only this one:
  watch: {
    activeTab() {
      if (this.activeTab === 'given' && this.givenItems.length === 0) {
        this.loadHistory();
      }
      if (this.activeTab === 'received' && this.receivedItems.length === 0) {
        this.loadHistory();
      }
    },
    selectedPeriod() {
      this.loadHistory("received");
      this.loadHistory("given");
    },
  },

  created() {
    this.loadHistory("received");
    this.loadHistory("given");   // ← load both on mount
    this.loadBalance();
  },
};
</script>

<style lang="scss" scoped>

.rh-root {
  background: #fff;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.rh-footer-desktop { display: block; }
.rh-footer-mobile {
  visibility: hidden;
  pointer-events: none;
  height: 0;
  overflow: hidden;
}

.rh-layout {
  display: grid;
  grid-template-columns: 220px 1fr 280px;
  align-items: stretch;
  padding-top: 2rem;
  padding-bottom: 3rem;
  flex: 1;
}

.rh-main {
  padding: 0 2rem;
  min-width: 0;
}

// Page header
.rh-page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;

  > div:first-child { flex: 1; }
}

.rh-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 4px;
}

.rh-sub {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0;
}

.rh-select {
  flex-shrink: 0;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 7px 30px 7px 12px;
  font-size: 0.85rem;
  color: #374151;
  background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%236b7280'/%3E%3C/svg%3E") no-repeat right 10px center;
  appearance: none;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: $primary;
    box-shadow: 0 0 0 3px rgba($primary, 0.12);
  }
}

// Tabs
.rh-tabs {
  display: flex;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 1.25rem;
}

.rh-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  transition: color 0.12s ease, border-color 0.12s ease;

  &:hover { color: #374151; }

  &--active {
    color: $primary;
    font-weight: 600;
    border-bottom-color: $primary;
  }
}

.rh-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 600;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 20px;
  padding: 1px 7px;
  min-width: 20px;

  .rh-tab--active & {
    background: #eff6ff;
    color: $primary;
  }
}

// List
.rh-list {
  display: flex;
  flex-direction: column;
}

.rh-item {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 0.625rem 0.5rem;
  border-bottom: 1px solid #f3f4f6;
  border-radius: 8px;
  transition: background 0.12s ease;

  &:last-child { border-bottom: none; }
  &:hover { background: #fafafa; }
}

.rh-avatar {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 2px solid #e5e7eb;
}

// Body — no gap, let children manage their own spacing
.rh-body {
   flex: 1;
    min-width: 0;
    display: flex;           // ← add this
    flex-direction: column;  // ← add this
    gap: 0;       
}

.rh-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0;
}

.rh-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #111827;
}

.rh-meta-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.rh-date-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;
}

.rh-date {
  font-size: 0.78rem;
  color: #6b7280;
  white-space: nowrap;
}

.rh-time {
  font-size: 0.72rem;
  color: #9ca3af;
  white-space: nowrap;
}

.rh-pts {
  font-size: 0.82rem;
  font-weight: 700;
  white-space: nowrap;

  &--positive { color: #16a34a; }
  &--negative { color: #e11d48; }
}

.rh-message {
  font-size: 0.85rem;
  color: #4b5563;
  margin-top: 2px;   /* add controlled spacing */
  line-height: 1.4;  /* slightly tighter text */
  margin: 0;
  padding: 0;
  display: block;
}

// Empty state
.rh-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 3rem 1rem;
  color: #9ca3af;
  text-align: center;

  p { font-size: 0.875rem; margin: 0; }
  svg { stroke: #d1d5db; }
}

// Right panel
.rh-panel {
  padding-left: 1.5rem;
  border-left: 1px solid #e5e7eb;
}

.rh-panel-sticky {
  position: sticky;
  top: 80px;
}

.rh-widget {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 1.1rem 1.25rem;
  margin-bottom: 0.875rem;

  &:last-child { margin-bottom: 0; }
}

.rh-widget-label {
  font-size: 0.78rem;
  font-weight: 500;
  color: #6b7280;
  margin: 0 0 6px;
}

.rh-balance-row {
  display: flex;
  align-items: baseline;
  gap: 5px;
  margin-bottom: 2px;
}

.rh-balance-num {
  font-size: 2.1rem;
  font-weight: 700;
  color: $primary;
  line-height: 1;
}

.rh-balance-unit {
  font-size: 0.95rem;
  color: #9ca3af;
}

.rh-balance-sub {
  font-size: 0.75rem;
  color: #9ca3af;
  margin: 0 0 1rem;
}

.rh-store-btn {
  width: 100%;
  background: $primary;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 10px 0;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover { background: darken($primary, 8%); }
}

.rh-widget-title {
  font-size: 0.72rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 10px;
}

.rh-summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 7px 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child { border-bottom: none; }
}

.rh-summary-label { font-size: 0.85rem; color: #4b5563; }

.rh-summary-val {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;

  &--green { color: #16a34a; }
  &--red   { color: #e11d48; }
}

// Tablet
@media (max-width: 1100px) {
  .rh-layout {
    grid-template-columns: 210px 1fr;
    grid-template-areas:
      "sidebar main"
      "panel   panel";
    align-items: start;
  }

  .rh-panel {
    grid-area: panel;
    border-left: none;
    border-top: 1px solid #e5e7eb;
    padding-left: 0;
    padding-top: 1.25rem;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 0.875rem;
  }

  .rh-panel-sticky { position: static; display: contents; }
  .rh-widget { margin-bottom: 0; }
}

// Mobile
@media (max-width: 768px) {
  .rh-footer-desktop { display: none !important; }
  .rh-footer-mobile {
    visibility: visible !important;
    pointer-events: auto !important;
    height: auto !important;
    overflow: visible !important;
  }

  .rh-layout { display: block; padding: 0; }
  .rh-main { padding: 1rem 1rem 90px; }
  .rh-panel { display: none; }

  .rh-page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.625rem;
  }

  .rh-select { width: 100%; }
  .rh-title { font-size: 1.25rem; }

  .rh-item-header {
    flex-wrap: wrap;
    gap: 4px;
  }

  .rh-meta-right {
    width: 100%;
    justify-content: space-between;
  }

  .rh-date-wrap {
    flex-direction: row;
    align-items: center;
    gap: 6px;
  }
}

// Skeleton loader
.rh-skeleton-wrap { display: flex; flex-direction: column; }

.rh-skeleton-item {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 0.75rem 0.5rem;
  border-bottom: 1px solid #f3f4f6;
}

.rh-skeleton-avatar {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: #e5e7eb;
  flex-shrink: 0;
  animation: rh-shimmer 1.4s infinite;
}

.rh-skeleton-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 4px;
}

.rh-skeleton-line {
  height: 10px;
  border-radius: 4px;
  background: #e5e7eb;
  animation: rh-shimmer 1.4s infinite;

  &--short { width: 35%; }
  &--long  { width: 80%; }
}

@keyframes rh-shimmer {
  0%   { opacity: 1; }
  50%  { opacity: 0.4; }
  100% { opacity: 1; }
}

// Error state
.rh-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 3rem 1rem;
  color: #e11d48;
  text-align: center;

  svg { stroke: #fecdd3; }
  p   { font-size: 0.875rem; margin: 0; color: #6b7280; }
}

.rh-retry-btn {
  background: none;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 6px 16px;
  font-size: 0.82rem;
  font-weight: 500;
  color: $primary;
  cursor: pointer;
  transition: background 0.12s;

  &:hover { background: #eff6ff; }
}
.rh-kudos-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #7c3aed;
  background: #f5f3ff;
  border: 1px solid #e9d5ff;
  padding: 3px 8px;
  border-radius: 20px;
  white-space: nowrap;

  svg { stroke: #7c3aed; flex-shrink: 0; }
}

.rh-name-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
}

.rh-event-tag {
  display: inline-flex;
  align-items: center;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 20px;
  white-space: nowrap;
  flex-shrink: 0;

  &--peer        { background: #eff6ff; color: $primary; }
  &--birthday    { background: #fff1f2; color: #e11d48; }
  &--anniversary { background: #fffbeb; color: #d97706; }
}

</style>